import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Redirect } from "react-router-dom/cjs/react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import ChangePassword from "../../components/modals/ChangePassword";
import UpdateContact from "../../components/modals/UpdateContact";
import UpdatePaymentInfo from "../../components/modals/UpdatePaymentInfo";
import ApiAccess from "../../components/setting/ApiAccess";
import MyApiPlan from "../../components/setting/MyApiPlan";
import { getFetchCustomer } from "../../store/actions/subscribeAction";
import { getEntitlement } from "../../store/actions/entitlementAction";
import { planCardData } from "../../utils/data";
import SubscriptionStatus from "./SubscriptionStatus";

const Setting = () => {
  const dispatch = useDispatch();
  const { uid, user } = useSelector((state) => state.authUser);
  const [contact, setContact] = useState(false);
  const [password, setPassword] = useState(false);
  const [payment, setPayment] = useState(false);
  const [cardDetails, setCardDetails] = useState("");
  // -1 is "still loading". null is "we asked and could not get an answer".
  const [entitlement, setEntitlement] = useState(-1);

  /*
      THE PLAN COMES FROM THE SERVER, NOT FROM A CACHED COLUMN (backend#103).

      This page used to decide everything from `user.stripe_customer_id`, a
      copy of a database column taken at sign-in and kept in localStorage. It
      is blank for all 776 free-plan customers and for comped accounts, and
      stale for anyone who paid after their last sign-in — so a paying
      customer was shown the free plan with no way to cancel (static#79,
      reported by Dave on dev and prod, 2026-09-18).

      GET /me/entitlement answers from our own records, with Stripe used only
      to fill in billing detail. Every branch below reads that answer.
  */
  const planKey = entitlement?.plan?.key;
  const apiPlan = planCardData?.find(
    (ele) => planKey && ele?.id === `api-${planKey}`
  );

  const toggleModal = (event) => {
    event.preventDefault();
    setContact(!contact);
  };
  const passwordToggle = (event) => {
    event.preventDefault();
    setPassword(!password);
  };
  const paymentToggle = (event) => {
    event.preventDefault();
    setPayment(!payment);
  };

  useEffect(() => {
    // Always asked. There is no condition under which the server should not
    // be allowed to answer — that condition was the bug.
    dispatch(getEntitlement((res) => setEntitlement(res)));

    // Saved cards are the one thing that genuinely needs a Stripe customer:
    // with no Stripe record there are no cards to fetch.
    if (user?.stripe_customer_id) {
      dispatch(
        getFetchCustomer(user?.stripe_customer_id, (res) => {
          setCardDetails(res);
        })
      );
    }
  }, []);

  if (entitlement === -1) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <Spinner> </Spinner>
      </div>
    );
  } else {
    return (
      <>
        {uid ? (
          <>
            <UpdateContact
              toggleModal={toggleModal}
              contact={contact}
              setContact={setContact}
            />
            <ChangePassword
              passwordToggle={passwordToggle}
              password={password}
              setPassword={setPassword}
            />
            <UpdatePaymentInfo
              paymentToggle={paymentToggle}
              payment={payment}
              setCardDetails={setCardDetails}
            />
            <div className="w-100 py-5 px-wide px-lg-narrow setting">
              <Container>
                <Row>
                  <Col md="12">
                    <h1>Account Settings</h1>
                  </Col>
                </Row>
                <Row>
                  <Col md="6" className="my-3">
                    <ul className="mb-5">
                      <li className="mb-4">
                        <h5>Contact Information</h5>
                      </li>
                      <li className="fw--600">{user?.username}</li>
                      <li>{user?.fullname}</li>
                      <li>{user?.email}</li>
                      <li>{user?.address}</li>
                      {user?.city && (
                        <li>
                          {user?.city}
                          {user?.state}
                          {user?.zip_code}
                        </li>
                      )}
                      {user?.company && <li>{user?.company}</li>}{" "}
                      <li className="mb-4">
                        {user?.webaddress && (
                          <a href={user?.webaddress} className="link-to">
                            {user?.webaddress}
                          </a>
                        )}
                      </li>
                      <li>
                        <Link onClick={toggleModal} className="link-to">
                          Update contact information
                        </Link>
                      </li>
                      <li>
                        <Link onClick={passwordToggle} className="link-to">
                          Change Password
                        </Link>
                      </li>
                    </ul>
                    {cardDetails && (
                      <ul className="mb-5">
                        <li className="mb-4">
                          <h5>Payment Information</h5>
                        </li>
                        <li>{cardDetails?.brand}</li>
                        <li>
                          ************
                          {cardDetails?.last4}
                        </li>
                        <li>
                          Expiration: {cardDetails?.exp_month}/
                          {cardDetails?.exp_year}
                        </li>
                        <li>
                          <Link onClick={paymentToggle} className="link-to">
                            Update payment method
                          </Link>
                        </li>
                      </ul>
                    )}
                  </Col>
                  <Col md="6" className="my-3">
                    <ul>
                      <li className="mb-4">
                        <h5>API Plan</h5>
                      </li>
                      <li>
                        {/*
                            Four cases, not two. "No plan at all" and "the free
                            plan" used to share one block that read "Sample
                            Course · Free · 5,000 calls" — true for a free
                            customer, false for the 443 accounts that never
                            chose a plan, and shown to paying customers too.

                            A fifth case, and it is not a plan state: null means
                            the request failed, because getEntitlement swallows
                            its own errors. Folding that into "none" told a
                            paying customer they had no plan whenever the
                            endpoint blipped — the very bug #103 exists to
                            prevent, arriving by a different road (#87 review).
                            A failure says so and offers a retry; it never
                            reports a plan we did not read.
                        */}
                        {!entitlement ? (
                          <ul className="mb-5">
                            <li className="fw--600">
                              We couldn't load your plan
                            </li>
                            <li className="mb-4">
                              Something went wrong on our end, so we're not
                              showing your plan rather than showing the wrong
                              one. Refresh to try again — nothing about your
                              subscription has changed.
                            </li>
                          </ul>
                        ) : entitlement.status === "none" ? (
                          <ul className="mb-5">
                            <li className="fw--600">No API plan yet</li>
                            <li className="mb-4">
                              Choose a plan to start using the Golfbert API.
                            </li>
                            <Link to="/api/plans" className="link-to">
                              See the API plans
                            </Link>
                          </ul>
                        ) : planKey === "test" ? (
                          <ul className="mb-5">
                            <li className="fw--600">Sample Course</li>
                            <li>
                              Access to our sample golf course for testing
                            </li>
                            <li>Free</li>
                            <li>Test our sample course for free</li>
                            <li className="mt-4 fw--600">Calls per month</li>
                            <li>5,000</li>
                            <li className="mb-4">
                              Paid plans allow additional calls
                            </li>

                            <Link to="/api/plans" className="link-to">
                              Upgrade to a premium API plan
                            </Link>
                          </ul>
                        ) : (
                          <MyApiPlan
                            apiPlan={apiPlan}
                            planLabel={entitlement?.plan?.label}
                          />
                        )}

                        {/*
                            Gated on having a plan, not on having a Stripe
                            record. SubscriptionStatus is the only route to
                            /main/cancel-membership anywhere in the app, and
                            that gate is what hid it from paying customers.
                        */}
                        {entitlement && entitlement.status !== "none" && (
                          <SubscriptionStatus entitlement={entitlement} />
                        )}
                      </li>
                    </ul>
                    {/* API Access (static#49) — the key,
                                            usage, rotation and call limit. */}
                    <ApiAccess onUpdatePayment={paymentToggle} />
                  </Col>
                </Row>
              </Container>
            </div>
          </>
        ) : (
          <Redirect to="/auth/signin" />
        )}
      </>
    );
  }
};

export default Setting;
